import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Info, Loader2, ShieldAlert } from 'lucide-react';
import {
  composicionApi,
  composicionKeys,
  type Advertencia,
} from '../../lib/composicion';

/**
 * Destino resaltado en el lienzo tras un clic en una fila del validador.
 * Viaja por CONTEXTO (no por props) porque entre la página y las tarjetas
 * hay componentes intermedios (LienzoItinerario, ColumnaFase) que no deben
 * enterarse del resaltado — mismo patrón que ArrastreContext.
 */
export interface Resaltado {
  faseId: string | null;
  sesionId: string | null;
  actividadId: string | null;
}

export const ResaltadoContext = createContext<Resaltado | null>(null);

/** Destino resaltado actual (o null). TarjetaSesion/FichaActividad lo consumen. */
export function useResaltado(): Resaltado | null {
  return useContext(ResaltadoContext);
}

/** «2 advertencias · 5 sugerencias», con singulares correctos. */
function resumen(nAdv: number, nInfo: number): string {
  const adv = `${nAdv} ${nAdv === 1 ? 'advertencia' : 'advertencias'}`;
  const info = `${nInfo} ${nInfo === 1 ? 'sugerencia' : 'sugerencias'}`;
  return `${adv} · ${info}`;
}

/**
 * Panel del validador (zona derecha del compositor, Lote 3): lista compacta
 * de advertencias de GET /procesos/{id}/validacion, ya ordenadas por el
 * backend (advertencias primero, info después; dentro, por posición en el
 * proceso). Clic en una fila → resalta y desplaza la tarjeta/ficha destino.
 * Se refresca cada 15 s y ADEMÁS junto al árbol: su query key anida bajo la
 * del árbol, así que toda invalidación del lienzo la arrastra sola.
 */
export function ValidadorPanel({
  procesoId,
  onResaltar,
}: {
  procesoId: string;
  /** La página guarda el destino y lo apaga sola a los 3 s. */
  onResaltar: (destino: Resaltado) => void;
}) {
  const validacion = useQuery({
    queryKey: composicionKeys.validacion(procesoId),
    queryFn: () => composicionApi.validacion(procesoId),
    enabled: procesoId !== '',
    refetchInterval: 15_000,
  });

  const filas = validacion.data?.advertencias ?? [];
  const nAdv = filas.filter((a) => a.nivel === 'advertencia').length;
  const nInfo = filas.length - nAdv;

  return (
    <section aria-label="Validador de la composición" className="flex h-full min-h-0 flex-col">
      <header className="border-b border-border px-4 py-3">
        <h3 className="flex items-center gap-1.5 font-display text-sm font-semibold text-content">
          <ShieldAlert className="h-4 w-4 text-cyan" aria-hidden="true" />
          Validador
        </h3>
        {validacion.data && filas.length > 0 && (
          <p className="mt-0.5 font-sans text-xs tabular-nums text-content-muted">
            {resumen(nAdv, nInfo)}
          </p>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {validacion.isLoading ? (
          <p className="flex items-center gap-1.5 px-2 py-2 font-sans text-xs text-content-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Revisando la composición…
          </p>
        ) : validacion.isError ? (
          <p className="px-2 py-2 font-sans text-xs text-content-muted">
            No se pudo revisar la composición. Se vuelve a intentar solo.
          </p>
        ) : filas.length === 0 ? (
          <p className="flex items-start gap-1.5 px-2 py-2 font-sans text-xs text-content-muted">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
            La composición no tiene advertencias. Publicá cuando quieras.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {filas.map((a, i) => (
              <li key={`${a.regla}-${i}`}>
                <FilaAdvertencia advertencia={a} onResaltar={onResaltar} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pie SIEMPRE visible: el contrato del validador en una línea. */}
      <footer className="border-t border-border px-4 py-3">
        <p className="font-sans text-xs text-content-muted">
          Las advertencias orientan, no bloquean: el proceso se puede publicar igual.
        </p>
      </footer>
    </section>
  );
}

/**
 * Fila compacta: icono por nivel + texto interpolado del backend. Si la
 * advertencia apunta a algo del lienzo es un botón que lo resalta; si es
 * global (sin destino) queda como texto plano.
 */
function FilaAdvertencia({
  advertencia,
  onResaltar,
}: {
  advertencia: Advertencia;
  onResaltar: (destino: Resaltado) => void;
}) {
  const icono =
    advertencia.nivel === 'advertencia' ? (
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
    ) : (
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
    );

  const tieneDestino =
    advertencia.fase_id != null ||
    advertencia.sesion_id != null ||
    advertencia.actividad_id != null;

  if (!tieneDestino) {
    return (
      <p className="flex items-start gap-2 rounded-md px-2 py-2 text-left font-sans text-xs text-content">
        {icono}
        <span className="min-w-0">{advertencia.texto}</span>
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={() =>
        onResaltar({
          faseId: advertencia.fase_id,
          sesionId: advertencia.sesion_id,
          actividadId: advertencia.actividad_id,
        })
      }
      title="Ver en el lienzo"
      className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left font-sans text-xs text-content transition-colors duration-fast hover:bg-surface-raised focus-visible:outline-none focus-visible:shadow-focus"
    >
      {icono}
      <span className="min-w-0">{advertencia.texto}</span>
    </button>
  );
}
